import { Badge } from 'antd';
import useModal from 'hooks/useModal';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { ReturnOrderListItemV2 } from 'services/api/refund';
import ConversationModal from 'views/Setting/Remind/components/ConversationModal';
import { useSetConversation, useGetConversationById } from 'hooks/business/lruConversation';
import { usePlatform } from '../hooks/usePlatformList';
import { useContactBuyer } from './useContactBuyer';

export type Row = Pick<
  ReturnOrderListItemV2,
  'orderSn' | 'region' | 'externalBuyerId' | 'externalStoreId' | 'buyerNick'
> & { returnSn?: string; reverseOrderId?: string };

export interface ContactBuyerProps {
  orderSn: string;
  region: General.UpperCaseRegion;
  externalBuyerId: string;
  externalStoreId: string;
  buyerNick: string;
  returnSn?: string;
  reverseOrderId?: string;
  conversation?: Conversation.Conversation;
  type: 'refund' | 'cancel';
  showUnread?: boolean;
  onClose?: () => void;
  children?: (props: { onClick: () => void }) => ReactNode;
}

export const ContactBuyer = (props: ContactBuyerProps) => {
  const getConversationById = useGetConversationById();
  const setConversation = useSetConversation();

  const {
    orderSn,
    region,
    externalBuyerId,
    externalStoreId,
    buyerNick,
    returnSn = '',
    reverseOrderId = '',
    conversation,
    type,
    showUnread = true,
    onClose,
    children,
  } = props;
  const { update, visible, hide, meta } = useModal(false);
  const channel = usePlatform()!;

  const trigger = useContactBuyer();

  const unreadCount = useMemo(() => {
    if (!conversation) {
      return;
    }
    const cur = getConversationById(conversation.conversationId);
    if (!cur) {
      return 0;
    }
    const { controlStatus, unreadCount } = cur;
    const displayUnreadCount = controlStatus;
    return displayUnreadCount ? unreadCount : 0;
  }, [conversation, getConversationById]);

  const onClick = useCallback(async () => {
    const res = await trigger({
      orderSn,
      region,
      externalBuyerId,
      externalStoreId,
      buyerNick,
      returnSn,
      reverseOrderId,
      type,
      channel,
      conversation,
    });
    if (!res) {
      return;
    }
    update(true, {
      conversationId: res.conversationId,
      buyerNick: res.buyerNick,
    });
  }, [
    buyerNick,
    channel,
    conversation,
    externalBuyerId,
    externalStoreId,
    orderSn,
    region,
    returnSn,
    reverseOrderId,
    trigger,
    type,
    update,
  ]);

  useEffect(() => {
    if (!conversation?.conversationId) {
      return;
    }
    setConversation(conversation.conversationId, conversation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.conversationId]);

  return (
    <>
      {showUnread ? (
        <Badge dot count={unreadCount}>
          {children?.({ onClick })}
        </Badge>
      ) : (
        children?.({ onClick })
      )}
      {meta && (
        <ConversationModal
          destroyOnClose
          forceAcquireControlReason="PROCESS_REFUND_OR_RETURN"
          record={meta as any}
          visible={visible}
          onCancel={() => {
            hide();
            onClose?.();
          }}
        />
      )}
    </>
  );
};
